import { AuthenticationService } from 'src/app/core/services/auth.service';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgColumnDataType, DgColumnWidths, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { firstValueFrom } from 'rxjs';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

const apiPath             =environment.eSimApi+eSimApiPaths.AlertsTriggered;
const exportFilenameSuffix="SIM-Alerts-Log-Data";
const popUpTitle          ="SIM Alerts Log";

@Component({
  selector: 'app-sim-alerts-log-embedded',
  templateUrl: './sim-alerts-log.component.html',
  styleUrls: ['./sim-alerts-log.component.scss']
})

export class SimAlertsLogEmbeddedComponent implements OnInit {

  @Input() companyId: number | undefined;
  @Input() deviceId : number | undefined;

  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;

  private gridViewDefaults!: GridViewDefaults;

  public curUser        : SystemUserDTO;
  public dgApiPath!     : DgApiPath | undefined;
  public dgActionColumn!: DgActionColumn | undefined;
  public dgColumns!     : Array<DgColumn>;
  public dgPopup!       : DgPopup | undefined;
  public dgProperties!  : DgProperties | undefined;
  public gotDgProperties: boolean=false;

  constructor(
    private authenticationService     : AuthenticationService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private encryptionService         : EncryptionService,
    private exceptionsService         : ExceptionsService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.companyId == undefined) {
        throw new Error("SimAlertsLogEmbeddedComponent - Company Id cannot be determined");
      }

      if (this.deviceId == undefined) {
        throw new Error("SimAlertsLogEmbeddedComponent - Device Id cannot be determined");
      }

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns();
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.refreshGet=() => this.setUpDgApiPathGet();

      await this.setUpDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPathGet(): Promise<void> {
    try {
      const companyIdEncResult=await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>this.companyId, true));
      const deviceIdEncResult =await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>this.deviceId));
      const companyIdEncSS    =companyIdEncResult.result;
      const deviceIdEnc       =deviceIdEncResult.result;

      this.dgApiPath!.get=environment.eSimApi +
                          eSimApiPaths.AlertsTriggered + '/' +
                          eSimApiPaths.AlertsTriggeredGetDeviceTriggeredAlerts +
                          "?companyIdEncSS=" + companyIdEncSS +
                          "&deviceIdEnc=" + deviceIdEnc;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn         =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.visible=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup=this.gridViewDefaults.setUpDgPopup(popUpTitle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties               =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.deviceId);
      this.dgProperties!.gridKey      ="EmbeddedSimAlertsLog";
      this.dgProperties!.showAddButton=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns() {
    try {
      let dgColumn: DgColumn | undefined;

      //ID
      dgColumn             =this.gridViewDefaults.setUpDgColumn("id", "ID", 0, DgColumnDataType.number);
      dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridShowId, false);
      dgColumn!.width      =DgColumnWidths.id;
      this.dgColumns       =[dgColumn!];

      //AlertType
      dgColumn=this.gridViewDefaults.setUpDgColumn("alertType", "Alert Type", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //AlertName
      dgColumn=this.gridViewDefaults.setUpDgColumn("alertName", "Alert Name", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //Country
      dgColumn=this.gridViewDefaults.setUpDgColumn("country", "Country", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //Threshold
      dgColumn=this.gridViewDefaults.setUpDgColumn("threshold", "Threshold", 0, DgColumnDataType.number);
      this.dgColumns.push(dgColumn!);

      //TriggeredDate
      dgColumn       =this.gridViewDefaults.setUpDgColumn("triggeredDate", "Date/Time", "", DgColumnDataType.datetime);
      dgColumn!.width=DgColumnWidths.dateWithTime;
      this.dgColumns.push(dgColumn!);

      //Action
      dgColumn=this.gridViewDefaults.setUpDgColumn("action", "Action", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
